import {
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  useRef,
} from "react";
import PropTypes from "prop-types";

import { getAutocompleteToken } from "../../../utils/autocomplete";
import Box from "@mui/material/Box";
import { ReactComponent as SearchIcon } from "../../../assets/search.svg";
import {
  InputAdornment,
  OutlinedInput,
  useMediaQuery,
  useTheme,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "./CommonSearchBar.css";

const CommonSearchBar = ({
  placeholder = "",
  isReset,
  onSearch,
  ref,
  value,
}) => {
  const [searchText, setSearchText] = useState("");
  useImperativeHandle(ref, () => ({
    clear: () => {
      setSearchText("");
    },
  }));

  useEffect(() => {
    setSearchText("");
  }, [isReset]);

  // Sync local search text with external value (e.g., Redux/localStorage-backed state)
  useEffect(() => {
    if (typeof value === "string") {
      setSearchText(value);
    }
  }, [value]);

  const handleSearch = useCallback(() => {
    if (searchText.trim() === "!") {
      return;
    }

    const value = searchText.trim();
    if (value) {
      onSearch(value);
      lastSubmittedRef.current = value;
    }
  }, [searchText, onSearch]);

  const handleSearchKeyDown = (event) => {
    if (event.key === "Enter" && searchText?.trim()) {
      handleSearch();
    }
  };
  const lastSubmittedRef = useRef("");

  const handleSearchChange = (event) => {
    const value = event.target.value;
    if (value.trim().length > 0 || value === "") {
      setSearchText(value);

      // If the input was previously submitted (tracked in lastSubmittedRef)
      // and the user cleared the field via backspace/delete, trigger a
      // cleared search so the parent can reset results.
      try {
        if (
          value === "" &&
          lastSubmittedRef.current &&
          lastSubmittedRef.current.length > 0
        ) {
          onSearch("", { cleared: true });
          lastSubmittedRef.current = "";
        }
      } catch (e) {
        console.warn("Error handling search change", e);
      }
    }
  };

  // Removed alert handling (unused)
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  return (
    <Box>
      <div className={isMobile ? "mobile-search-bar" : "common-search-bar"}>
        <OutlinedInput
          id="search-leads"
          className="search-input"
          placeholder={placeholder}
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          value={searchText}
          autoComplete={getAutocompleteToken("search")}
          endAdornment={
            <InputAdornment position="end">
              <div style={{ position: "relative", width: 20 }}>
                {searchText && searchText.length > 0 && (
                  <IconButton
                    aria-label="clear search"
                    size="small"
                    onClick={() => {
                      // Clear the input and always notify parent so it can reset results
                      setSearchText("");
                      try {
                        onSearch("", { cleared: true });
                      } catch (e) {
                        console.warn("Error clearing search", e);
                      }
                      lastSubmittedRef.current = "";
                    }}
                    sx={{
                      position: "absolute",
                      right: 20,
                      top: "50%",
                      transform: "translateY(-50%)",
                      padding: "4px",
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}

                <div
                  className="search-icon-wrapper"
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: 0,
                    transform: "translateY(-50%)",
                  }}
                >
                  <SearchIcon
                    onClick={handleSearch}
                    style={{ cursor: "pointer" }}
                  />
                </div>
              </div>
            </InputAdornment>
          }
          fullWidth
          sx={{
            "& .MuiOutlinedInput-root": {
              height: "40px",
            },
            borderRadius: "56px",
            "& .MuiInputBase-input": {
              textOverflow: "ellipsis",
            },
            "& .MuiInputBase-input::placeholder": {
              textOverflow: "ellipsis",
            },
          }}
        />
      </div>
    </Box>
  );
};

export default CommonSearchBar;

CommonSearchBar.propTypes = {
  placeholder: PropTypes.string,
  isReset: PropTypes.bool,
  onSearch: PropTypes.func.isRequired,
  value: PropTypes.string,
  ref: PropTypes.any,
};
